import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { trackLead } from "@/utils/metaPixel";
import { 
  Store, 
  Heart, 
  Instagram, 
  CheckCircle2,
  Users,
  TrendingUp,
  Euro,
  ArrowRight,
  Sparkles
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VendorPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    brand_name: "",
    phone: "",
    website: "",
    instagram: "",
    category: "",
    description: "",
    fee_accepted: false,
    consent: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showFeeError, setShowFeeError] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === "fee_accepted") {
      setShowFeeError(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Bitte gib deinen Namen ein");
      return;
    }
    if (!formData.email.trim()) {
      toast.error("Bitte gib deine E-Mail-Adresse ein");
      return;
    }
    if (!formData.brand_name.trim()) {
      toast.error("Bitte gib deinen Brand-Namen ein");
      return;
    }
    if (!formData.category) {
      toast.error("Bitte wähle eine Kategorie");
      return;
    }
    if (!formData.fee_accepted) {
      setShowFeeError(true);
      return;
    }
    if (!formData.consent) {
      toast.error("Bitte stimme der Datenschutzerklärung zu");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API}/vendor`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Fire STANDARD Lead event - NO PARAMETERS
        trackLead();
        navigate("/vendor-danke");
      } else {
        toast.error(data.detail || data.message || "Ein Fehler ist aufgetreten");
      }
    } catch (error) {
      console.error("Vendor application error:", error);
      toast.error("Verbindungsfehler. Bitte versuche es erneut.");
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById("vendor-form")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] font-body">
      {/* Hero Section */}
      <section className="relative px-4 py-12 md:py-20">
        <div className="max-w-4xl mx-auto">
          {/* Brand Tag */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#8B2332]/10 text-[#8B2332] text-sm font-medium mb-6">
            <Store className="w-4 h-4" />
            <span>Für Brands & Aussteller</span>
          </div>

          {/* Headline */}
          <h1 
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-[#2D2D2D] leading-tight mb-4"
            data-testid="vendor-headline"
          >
            Werde Teil des{" "}
            <span className="text-[#8B2332] italic">Hijab & Halal</span>{" "}
            Pop-ups
          </h1>

          <p className="text-lg md:text-xl text-[#6B6B6B] mb-8 max-w-2xl">
            Präsentiere deine Produkte vor über 1.000 muslimischen Frauen in Hamburg. 
            Sichere dir jetzt deinen Stand.
          </p>

          {/* Key Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 border border-[#EFEBE5]">
              <Users className="w-6 h-6 text-[#8B2332] mb-2" />
              <div className="font-heading text-2xl text-[#2D2D2D]">~1.200</div>
              <p className="text-[#6B6B6B] text-sm">Besucherinnen</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-[#EFEBE5]">
              <Store className="w-6 h-6 text-[#8B2332] mb-2" />
              <div className="font-heading text-2xl text-[#2D2D2D]">40–50</div>
              <p className="text-[#6B6B6B] text-sm">Brands</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-[#EFEBE5]">
              <TrendingUp className="w-6 h-6 text-[#8B2332] mb-2" />
              <div className="font-heading text-2xl text-[#2D2D2D]">8h</div>
              <p className="text-[#6B6B6B] text-sm">Event-Dauer</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-[#EFEBE5]">
              <Euro className="w-6 h-6 text-[#8B2332] mb-2" />
              <div className="font-heading text-2xl text-[#2D2D2D]">ab 250€</div>
              <p className="text-[#6B6B6B] text-sm">inkl. MwSt.</p>
            </div>
          </div>

          <Button
            onClick={scrollToForm}
            className="bg-[#8B2332] hover:bg-[#7A1E2B] text-white rounded-full px-8 py-6 text-lg font-medium transition-all hover:-translate-y-1 hover:shadow-xl"
            data-testid="vendor-cta-hero"
          >
            Jetzt bewerben
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Info Box */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#8B2332]/5 border border-[#8B2332]/20 rounded-2xl p-6 md:p-8">
            <h2 className="font-heading text-2xl text-[#2D2D2D] mb-4 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-[#8B2332]" />
              Was dich erwartet
            </h2>
            <ul className="space-y-3 text-[#6B6B6B]">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#8B2332] mt-0.5 flex-shrink-0" />
                <span>Direkter Zugang zu über 1.000 kaufkräftigen Kundinnen</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#8B2332] mt-0.5 flex-shrink-0" />
                <span>Professionelle Event-Location: Grand Palais, Hamburg</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#8B2332] mt-0.5 flex-shrink-0" />
                <span>Marketing & Promotion durch EDYT & BLOOM</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#8B2332] mt-0.5 flex-shrink-0" />
                <span>Networking mit anderen muslimischen Brands</span>
              </li>
            </ul>
            <div className="mt-6 pt-6 border-t border-[#8B2332]/20">
              <p className="text-[#2D2D2D] font-medium">
                Standgebühren starten ab <span className="text-[#8B2332] font-semibold">250 € inkl. MwSt.</span> (je nach Standgröße/Setup).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 px-4 bg-[#FAF7F2]" id="vendor-form">
        <div className="max-w-xl mx-auto">
          <h2 className="font-heading text-3xl text-[#2D2D2D] text-center mb-8">
            Bewerbung als Aussteller
          </h2>

          <div 
            className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-[#EFEBE5]"
            data-testid="vendor-form-container"
          >
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Personal Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-1">
                    Dein Name *
                  </label>
                  <Input
                    type="text"
                    placeholder="Vollständiger Name"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    className="h-12 bg-[#FAF7F2] border-[#DCD6CF] focus:border-[#8B2332] focus:ring-[#8B2332]"
                    data-testid="vendor-input-name"
                    disabled={isLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-1">
                    E-Mail *
                  </label>
                  <Input
                    type="email"
                    placeholder="deine@email.de"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className="h-12 bg-[#FAF7F2] border-[#DCD6CF] focus:border-[#8B2332] focus:ring-[#8B2332]"
                    data-testid="vendor-input-email"
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Brand Info */}
              <div>
                <label className="block text-sm font-medium text-[#2D2D2D] mb-1">
                  Brand-Name *
                </label>
                <Input
                  type="text"
                  placeholder="Name deiner Marke"
                  value={formData.brand_name}
                  onChange={(e) => handleChange("brand_name", e.target.value)}
                  className="h-12 bg-[#FAF7F2] border-[#DCD6CF] focus:border-[#8B2332] focus:ring-[#8B2332]"
                  data-testid="vendor-input-brand"
                  disabled={isLoading}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-1">
                    Telefon
                  </label>
                  <Input
                    type="tel"
                    placeholder="+49..."
                    value={formData.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    className="h-12 bg-[#FAF7F2] border-[#DCD6CF] focus:border-[#8B2332] focus:ring-[#8B2332]"
                    data-testid="vendor-input-phone"
                    disabled={isLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2D2D2D] mb-1">
                    Instagram
                  </label>
                  <Input
                    type="text"
                    placeholder="@deinbrand"
                    value={formData.instagram}
                    onChange={(e) => handleChange("instagram", e.target.value)}
                    className="h-12 bg-[#FAF7F2] border-[#DCD6CF] focus:border-[#8B2332] focus:ring-[#8B2332]"
                    data-testid="vendor-input-instagram"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2D2D2D] mb-1">
                  Website
                </label>
                <Input
                  type="url"
                  placeholder="https://..."
                  value={formData.website}
                  onChange={(e) => handleChange("website", e.target.value)}
                  className="h-12 bg-[#FAF7F2] border-[#DCD6CF] focus:border-[#8B2332] focus:ring-[#8B2332]"
                  data-testid="vendor-input-website"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2D2D2D] mb-1">
                  Kategorie *
                </label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => handleChange("category", value)}
                  disabled={isLoading}
                >
                  <SelectTrigger 
                    className="h-12 bg-[#FAF7F2] border-[#DCD6CF] focus:border-[#8B2332] focus:ring-[#8B2332]"
                    data-testid="vendor-select-category"
                  >
                    <SelectValue placeholder="Wähle eine Kategorie" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="modest-fashion">Modest Fashion</SelectItem>
                    <SelectItem value="hijabs">Hijabs & Kopfbedeckungen</SelectItem>
                    <SelectItem value="abayas">Abayas & Kleider</SelectItem>
                    <SelectItem value="jewelry">Schmuck & Accessoires</SelectItem>
                    <SelectItem value="kids">Kindermode</SelectItem>
                    <SelectItem value="beauty">Beauty & Pflege</SelectItem>
                    <SelectItem value="food">Food & Snacks</SelectItem>
                    <SelectItem value="home">Home & Deko</SelectItem>
                    <SelectItem value="other">Sonstiges</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2D2D2D] mb-1">
                  Kurze Beschreibung deiner Produkte
                </label>
                <textarea
                  placeholder="Erzähl uns kurz, was du verkaufst..."
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  className="w-full min-h-[100px] px-4 py-3 bg-[#FAF7F2] border border-[#DCD6CF] rounded-lg focus:border-[#8B2332] focus:ring-1 focus:ring-[#8B2332] focus:outline-none resize-none"
                  data-testid="vendor-input-description"
                  disabled={isLoading}
                />
              </div>

              {/* Fee Acceptance */}
              <div className={`p-4 rounded-xl border-2 ${showFeeError ? 'border-red-400 bg-red-50' : 'border-[#EFEBE5] bg-[#FAF7F2]'}`}>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="fee_accepted"
                    checked={formData.fee_accepted}
                    onCheckedChange={(checked) => handleChange("fee_accepted", checked)}
                    className="mt-1 border-[#8B2332] data-[state=checked]:bg-[#8B2332] data-[state=checked]:border-[#8B2332]"
                    data-testid="vendor-checkbox-fee"
                    disabled={isLoading}
                  />
                  <label htmlFor="fee_accepted" className="cursor-pointer">
                    <span className="font-medium text-[#2D2D2D]">
                      Standgebühr ab 250 € inkl. MwSt. – kommt das für dich grundsätzlich in Frage? *
                    </span>
                  </label>
                </div>
                {showFeeError && (
                  <p className="text-red-600 text-sm mt-3 pl-7">
                    Dann passt es aktuell leider nicht – unsere Standgebühren starten ab 250 € inkl. MwSt.
                  </p>
                )}
              </div>

              {/* Consent */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="consent"
                  checked={formData.consent}
                  onCheckedChange={(checked) => handleChange("consent", checked)}
                  className="mt-1 border-[#8B2332] data-[state=checked]:bg-[#8B2332] data-[state=checked]:border-[#8B2332]"
                  data-testid="vendor-checkbox-consent"
                  disabled={isLoading}
                />
                <label htmlFor="consent" className="text-sm text-[#6B6B6B] leading-relaxed cursor-pointer">
                  Ich stimme der <Link to="/datenschutz" className="text-[#8B2332] hover:underline">Datenschutzerklärung</Link> zu und möchte Infos zum Event erhalten.
                </label>
              </div>

              <Button
                type="submit"
                className="w-full h-14 bg-[#8B2332] hover:bg-[#7A1E2B] text-white text-lg font-medium rounded-full transition-all hover:-translate-y-1 hover:shadow-xl"
                data-testid="vendor-submit-button"
                disabled={isLoading}
              >
                {isLoading ? "Wird gesendet..." : "Bewerbung absenden"}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-white" data-testid="vendor-faq-section">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-heading text-3xl text-[#2D2D2D] text-center mb-12">
            Häufige Fragen für Aussteller
          </h2>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="bg-[#FAF7F2] rounded-xl border border-[#EFEBE5] px-6">
              <AccordionTrigger className="text-[#2D2D2D] font-medium hover:no-underline" data-testid="vendor-faq-1">
                Was kostet ein Stand?
              </AccordionTrigger>
              <AccordionContent className="text-[#6B6B6B]">
                Standgebühren starten ab 250 € inkl. MwSt. (je nach Standgröße/Setup). Die genauen Konditionen besprechen wir nach deiner Bewerbung.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="bg-[#FAF7F2] rounded-xl border border-[#EFEBE5] px-6">
              <AccordionTrigger className="text-[#2D2D2D] font-medium hover:no-underline" data-testid="vendor-faq-2">
                Wann findet das Event statt?
              </AccordionTrigger>
              <AccordionContent className="text-[#6B6B6B]">
                Sonntag, 08.02.2026, 11–19 Uhr im Grand Palais, Osterrade 20, 21031 Hamburg.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="bg-[#FAF7F2] rounded-xl border border-[#EFEBE5] px-6">
              <AccordionTrigger className="text-[#2D2D2D] font-medium hover:no-underline" data-testid="vendor-faq-3">
                Was ist im Standpreis enthalten?
              </AccordionTrigger>
              <AccordionContent className="text-[#6B6B6B]">
                Standplatz, Tisch, Zugang zum Event, Marketing durch EDYT & BLOOM Kanäle. Weitere Details nach Bewerbung.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="bg-[#FAF7F2] rounded-xl border border-[#EFEBE5] px-6">
              <AccordionTrigger className="text-[#2D2D2D] font-medium hover:no-underline" data-testid="vendor-faq-4">
                Wie viele Besucherinnen kommen?
              </AccordionTrigger>
              <AccordionContent className="text-[#6B6B6B]">
                Beim letzten Event hatten wir ca. 1.200 Besucherinnen – ausschließlich muslimische Frauen.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-white border-t border-[#EFEBE5]">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2 text-[#6B6B6B]">
              <Heart className="w-4 h-4 text-[#8B2332]" />
              <span className="font-medium text-[#2D2D2D]">EDYT & BLOOM</span>
            </div>
            
            <div className="flex items-center gap-6 text-sm">
              <Link to="/pass" className="text-[#6B6B6B] hover:text-[#8B2332] transition-colors">
                Besucher-Pass
              </Link>
              <Link to="/impressum" className="text-[#6B6B6B] hover:text-[#8B2332] transition-colors">
                Impressum
              </Link>
              <Link to="/datenschutz" className="text-[#6B6B6B] hover:text-[#8B2332] transition-colors">
                Datenschutz
              </Link>
              <a 
                href="https://www.instagram.com/edytandbloom/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#6B6B6B] hover:text-[#8B2332] transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VendorPage;
